import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  CheckCircle,
  ArrowRight,
  Mail,
  MapPin,
  Users,
  ClipboardList,
  Brain,
} from 'lucide-react';

const credentials = [
  {
    label: 'Federal cybersecurity background',
    sub: 'Defense and intelligence agency experience · NIST RMF',
  },
  {
    label: 'Certified IT professionals',
    sub: 'CompTIA A+ · Security+ certified team members',
  },
  {
    label: 'CMMC compliance expertise',
    sub: 'NIST SP 800-171 · SPRS scoring · System Security Plans',
  },
  {
    label: 'Maryland WOSB certified',
    sub: 'Woman-owned, Maryland-based small business',
  },
  {
    label: 'AI-powered assessment tools',
    sub: 'Enterprise quality at small business prices',
  },
];

const values = [
  {
    icon: Shield,
    title: 'Honest assessments',
    desc: "We tell you exactly where you stand — no inflated findings, no upselling. If you don't have a gap, we'll tell you.",
  },
  {
    icon: Users,
    title: 'Built for small business',
    desc: "We designed every service around the reality that you don't have a full IT team. Plain English, practical steps, real outcomes.",
  },
  {
    icon: ClipboardList,
    title: 'Federal rigor, local scale',
    desc: "Our team applies the same frameworks used to protect federal agencies — scaled and priced for a dental practice or law firm.",
  },
  {
    icon: Brain,
    title: 'AI-powered tools',
    desc: 'We use AI to automate the parts that used to require a large team — so you get enterprise-quality results faster and at lower cost.',
  },
];

const About: React.FC = () => {
  return (
    <div className="w-full">

      {/* ── HERO ──────────────────────────────────────────── */}
      <section className="bg-[#001a4d] py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-5">
            <Shield size={14} className="text-[#0B8A8F]" />
            <span className="text-white/80 text-xs font-bold uppercase tracking-widest">About Us</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight">
            Federal expertise. Baltimore roots.<br className="hidden sm:block" /> Built for small business.
          </h1>
          <p className="text-white/70 text-lg leading-relaxed max-w-2xl mx-auto">
            We brought federal cybersecurity experience to the businesses that need it most —
            and built tools to make it affordable.
          </p>
        </div>
      </section>

      {/* ── OUR STORY ─────────────────────────────────────── */}
      <section className="bg-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">

            {/* Left: story */}
            <div>
              <span className="text-xs font-black uppercase tracking-widest text-[#0B8A8F] mb-3 block">Our Story</span>
              <h2 className="text-3xl font-extrabold text-[#001a4d] mb-6 leading-tight">
                We saw a gap — and built something to fix it
              </h2>
              <div className="space-y-4 text-gray-600 text-base leading-relaxed">
                <p>
                  Cyvantix Solutions was founded by cybersecurity professionals who spent years
                  protecting federal agencies and large defense contractors at the highest levels —
                  then looked around and saw that the small businesses running Baltimore had no one
                  in their corner.
                </p>
                <p>
                  The same ransomware attacks hitting hospitals and law firms. The same AI-powered
                  fraud targeting community colleges. The same compliance deadlines bearing down on
                  defense subcontractors. But none of the resources — no dedicated security team,
                  no enterprise budget, no one to call.
                </p>
                <p>
                  So we built CyvaForge, our proprietary security assessment platform, specifically
                  for businesses without a dedicated IT security team. It lets us deliver the same
                  quality of assessment that large enterprises pay six figures for — at a price a
                  dental practice or law firm can actually afford.
                </p>
                <p>
                  Our team includes certified IT professionals holding CompTIA A+ and Security+
                  certifications alongside our federal cybersecurity background — so whether you
                  need day-to-day IT support, a full security assessment, or CMMC compliance
                  readiness, we have the credentials and experience to back it up.
                </p>
                <p>
                  We are a Maryland-based, woman-owned small business. We know this market, we
                  live in this community, and we are invested in keeping Baltimore businesses safe.
                </p>
              </div>
              <p className="mt-5 text-sm text-gray-400 italic">
                Our assessments run on CyvaForge, our proprietary AI security platform built in-house — not off-the-shelf tools.
              </p>
            </div>

            {/* Right: credential badges */}
            <div className="flex flex-col gap-4">
              <span className="text-xs font-black uppercase tracking-widest text-[#0B8A8F] mb-1 block">Credentials & Background</span>
              {credentials.map((cred, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 bg-gray-50 border border-gray-100 rounded-2xl px-6 py-5"
                >
                  <div className="w-9 h-9 rounded-xl bg-[#0B8A8F] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="font-bold text-[#001a4d] text-sm">{cred.label}</div>
                    <div className="text-gray-500 text-xs mt-0.5">{cred.sub}</div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ── VALUES ────────────────────────────────────────── */}
      <section className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-black uppercase tracking-widest text-[#0B8A8F] mb-3 block">How We Work</span>
            <h2 className="text-3xl font-extrabold text-[#001a4d]">What makes us different</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-7 flex gap-5 items-start">
                  <div className="w-11 h-11 rounded-xl bg-[#001a4d] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-white" strokeWidth={1.8} />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-[#001a4d] mb-2">{v.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CMMC CALLOUT ──────────────────────────────────── */}
      <section className="bg-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-8 py-7 flex flex-col sm:flex-row gap-5 items-start">
            <div className="w-11 h-11 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-6 h-6 text-white" strokeWidth={1.8} />
            </div>
            <div>
              <h3 className="font-extrabold text-[#001a4d] mb-1">CMMC Compliance Readiness</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                With $57 billion in Maryland defense contract activity, CMMC compliance is a
                live requirement — not a future deadline. While the DoD suspended its Phase 2
                third-party assessment requirement in July 2026, self-assessments are in effect
                now and the 110 NIST SP 800-171 controls must be implemented to keep contracts.
                Our team has the federal background to guide you through every step.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center gap-1.5 mt-4 text-sm font-bold text-[#0B8A8F] hover:underline"
              >
                Learn about CMMC Compliance Readiness <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────── */}
      <section className="bg-[#001a4d] py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
            Ready to get started?
          </h2>
          <p className="text-white/70 text-base mb-8">
            Book a free CyvaCheck — no obligation, no jargon, just a clear picture of where you stand.
          </p>
          <a
            href="mailto:info@cyvantixsolutions.com?subject=Book%20Free%20CyvaCheck"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#0B8A8F] text-white font-bold rounded-xl hover:bg-[#097a7f] transition-all shadow-lg text-base mb-7"
          >
            Book Your Free CyvaCheck <ArrowRight size={18} />
          </a>
          <div className="flex items-center justify-center gap-2 text-white/50 text-sm">
            <Mail size={14} />
            <a href="mailto:info@cyvantixsolutions.com" className="hover:text-white transition-colors">
              info@cyvantixsolutions.com
            </a>
            <span className="mx-2">·</span>
            <MapPin size={14} />
            <span>Baltimore, MD</span>
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;
